import React, { useState, useEffect } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { TeamMember } from '../types';
import { HiOutlinePencil, HiOutlineTrash, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Team: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    nameAr: '',
    nameEn: '',
    positionAr: '',
    positionEn: '',
    bioAr: '',
    bioEn: '',
    image: null as File | null,
    isFounder: false,
    isPinned: false,
    orderIndex: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(api.team.getAll());
      setMembers(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل أعضاء الفريق');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      nameAr: member.nameAr,
      nameEn: member.nameEn,
      positionAr: member.positionAr,
      positionEn: member.positionEn,
      bioAr: member.bioAr || '',
      bioEn: member.bioEn || '',
      image: null,
      isFounder: member.isFounder,
      isPinned: member.isPinned,
      orderIndex: member.orderIndex,
      isActive: member.isActive,
    });
    setImagePreview(member.image ? `${API_URL.replace('/api', '')}${member.image}` : null);
    setShowForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العضو؟')) return;

    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.team.delete(id));
      setSuccess('تم حذف العضو بنجاح');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف العضو');
    }
  };

  const handleToggleActive = async (member: TeamMember) => {
    try {
      setError('');
      setSuccess('');
      const formData = new FormData();
      formData.append('isActive', String(!member.isActive));
      
      await axiosInstance.put(api.team.update(member.id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess('تم تحديث حالة العضو بنجاح');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحديث حالة العضو');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const submitFormData = new FormData();
      submitFormData.append('nameAr', formData.nameAr);
      submitFormData.append('nameEn', formData.nameEn);
      submitFormData.append('positionAr', formData.positionAr);
      submitFormData.append('positionEn', formData.positionEn);
      submitFormData.append('bioAr', formData.bioAr);
      submitFormData.append('bioEn', formData.bioEn);
      submitFormData.append('isFounder', String(formData.isFounder));
      submitFormData.append('isPinned', String(formData.isPinned));
      submitFormData.append('orderIndex', String(formData.orderIndex));
      submitFormData.append('isActive', String(formData.isActive));

      if (formData.image) {
        submitFormData.append('image', formData.image);
      }

      if (editingMember) {
        await axiosInstance.put(api.team.update(editingMember.id), submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSuccess('تم تحديث العضو بنجاح');
      } else {
        await axiosInstance.post(api.team.create, submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSuccess('تم إضافة العضو بنجاح');
      }

      setShowForm(false);
      setEditingMember(null);
      setImagePreview(null);
      setFormData({
        nameAr: '',
        nameEn: '',
        positionAr: '',
        positionEn: '',
        bioAr: '',
        bioEn: '',
        image: null,
        isFounder: false,
        isPinned: false,
        orderIndex: 0,
        isActive: true,
      });
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || `فشل ${editingMember ? 'تحديث' : 'إضافة'} العضو`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMember(null);
    setImagePreview(null);
    setFormData({
      nameAr: '',
      nameEn: '',
      positionAr: '',
      positionEn: '',
      bioAr: '',
      bioEn: '',
      image: null,
      isFounder: false,
      isPinned: false,
      orderIndex: 0,
      isActive: true,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getImageUrl = (imagePath?: string) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_URL.replace('/api', '')}${imagePath}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              فريق العمل
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          {/* Header */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold" style={{ color: '#ffffff' }}>قائمة أعضاء الفريق</h2>
            <button
              onClick={() => {
                handleCancel();
                setShowForm(true);
              }}
              className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{
                background: 'var(--button-gradient)',
              }}
            >
              إضافة عضو جديد
            </button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingMember ? 'تعديل العضو' : 'إضافة عضو جديد'}
                    </h2>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        الصورة الشخصية
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {imagePreview && (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الاسم بالعربية *
                        </label>
                        <input
                          type="text"
                          value={formData.nameAr}
                          onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الاسم بالإنجليزية *
                        </label>
                        <input
                          type="text"
                          value={formData.nameEn}
                          onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المسمى الوظيفي بالعربية *
                        </label>
                        <input
                          type="text"
                          value={formData.positionAr}
                          onChange={(e) => setFormData({ ...formData, positionAr: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المسمى الوظيفي بالإنجليزية *
                        </label>
                        <input
                          type="text"
                          value={formData.positionEn}
                          onChange={(e) => setFormData({ ...formData, positionEn: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          السيرة الذاتية بالعربية
                        </label>
                        <textarea
                          value={formData.bioAr}
                          onChange={(e) => setFormData({ ...formData, bioAr: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          السيرة الذاتية بالإنجليزية
                        </label>
                        <textarea
                          value={formData.bioEn}
                          onChange={(e) => setFormData({ ...formData, bioEn: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ترتيب العرض
                        </label>
                        <input
                          type="number"
                          value={formData.orderIndex}
                          onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isFounder}
                          onChange={(e) => setFormData({ ...formData, isFounder: e.target.checked })}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="mr-2 text-sm font-medium text-gray-700">
                          مؤسس
                        </span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isPinned}
                          onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="mr-2 text-sm font-medium text-gray-700">
                          مثبت
                        </span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="mr-2 text-sm font-medium text-gray-700">
                          نشط
                        </span>
                      </label>
                    </div>

                    <div className="flex gap-4 justify-end pt-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                        style={{
                          background: 'var(--button-gradient)',
                        }}
                      >
                        {editingMember ? 'تحديث' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Grid/Table */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا يوجد أعضاء في الفريق</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.map((member) => {
                const imageUrl = getImageUrl(member.image);
                return (
                  <div
                    key={member.id}
                    className="rounded-lg overflow-hidden transition-all"
                    style={{
                      background: 'rgba(8, 42, 73, 0.6)',
                      border: '1px solid transparent',
                      color: '#ffffff',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={member.nameAr}
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>لا توجد صورة</span>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-xl font-semibold" style={{ color: '#ffffff' }}>{member.nameAr}</h3>
                        {member.isFounder && (
                          <span 
                            className="px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor: 'rgba(234, 179, 8, 0.2)',
                              color: '#fde047',
                            }}
                          >
                            مؤسس
                          </span>
                        )}
                        {member.isPinned && (
                          <span 
                            className="px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor: 'rgba(59, 130, 246, 0.2)',
                              color: '#93c5fd',
                            }}
                          >
                            مثبت
                          </span>
                        )}
                      </div>
                      <p className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{member.positionAr}</p>
                      {member.bioAr && (
                        <p className="text-sm mb-4 line-clamp-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{member.bioAr}</p>
                      )}
                      <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            member.isActive
                              ? 'bg-green-500 bg-opacity-20 text-green-300'
                              : 'bg-gray-500 bg-opacity-20 text-gray-300'
                          }`}
                        >
                          {member.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => handleEdit(member)}
                            className="p-2 rounded-lg transition-all hover:bg-blue-500 hover:bg-opacity-20"
                            style={{ color: '#477CD7' }}
                            title="تعديل"
                          >
                            {React.createElement(HiOutlinePencil as any, { size: 18 })}
                          </button>
                          <button
                            onClick={() => handleToggleActive(member)}
                            className="p-2 rounded-lg transition-all hover:bg-yellow-500 hover:bg-opacity-20"
                            style={{ color: member.isActive ? '#F59E0B' : '#10B981' }}
                            title={member.isActive ? 'تعطيل' : 'تفعيل'}
                          >
                            {member.isActive 
                              ? React.createElement(HiOutlineEyeOff as any, { size: 18 })
                              : React.createElement(HiOutlineEye as any, { size: 18 })
                            }
                          </button>
                          {!member.isFounder && (
                            <button
                              onClick={() => handleDelete(member.id)}
                              className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                              style={{ color: '#EF4444' }}
                              title="حذف"
                            >
                              {React.createElement(HiOutlineTrash as any, { size: 18 })}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Team;

